import { useEffect, useRef } from 'react'
import { useAlertWindowMatchStore } from '@/features/alert-windows/alert-window-match-store'
import { useAlertWindowsViewStore } from '@/features/alert-windows/alert-windows-view-store'
import { usePriceAlertsViewStore } from '@/features/price-alerts/price-alerts-view-store'
import type { WindowConfig } from '@/workspace/types'

/**
 * Drops the transient per-panel view state of the alert panels (filter, form,
 * counts, an Alert Window's fires) once the panel is REMOVED from the layout.
 *
 * The signal is the panel id leaving `windowConfigs`, which is what
 * `cleanupPanel` / `closeTab` do on a real close. A tab switch leaves the
 * config in place (only the body unmounts), so the user's filter survives it.
 * Only ids this hook has seen are cleared, so another workspace's entries are
 * never touched.
 */
export function useAlertPanelViewCleanup(windowConfigs: Record<string, WindowConfig>) {
  const seen = useRef(new Map<string, WindowConfig['componentType']>())
  useEffect(() => {
    const prev = seen.current
    const next = new Map<string, WindowConfig['componentType']>()
    for (const [id, cfg] of Object.entries(windowConfigs)) {
      if (cfg.componentType === 'price-alerts' || cfg.componentType === 'alert-windows') {
        next.set(id, cfg.componentType)
      }
    }
    for (const [id, type] of prev) {
      if (next.has(id)) continue
      if (type === 'price-alerts') usePriceAlertsViewStore.getState().clearPanel(id)
      else {
        useAlertWindowsViewStore.getState().clearPanel(id)
        useAlertWindowMatchStore.getState().clear(id)
      }
    }
    seen.current = next
  }, [windowConfigs])
}
